import React, { useState } from 'react';
import { CreditCard, Smartphone, ChevronDown, Check, AlertCircle, Calendar, User, Lock } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { validateUpiId } from '../lib/payments';
import PhonePeButton from './PhonePeButton';

type PaymentMethodProps = {
  onPaymentSelect: (method: string) => void;
  amount: number;
  orderId: string;
};

const PaymentMethods: React.FC<PaymentMethodProps> = ({ onPaymentSelect, amount, orderId }) => {
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [upiId, setUpiId] = useState('');
  const [upiError, setUpiError] = useState<string | null>(null);
  const [isUpiExpanded, setIsUpiExpanded] = useState(false);
  const { currentCurrency } = useCurrency();

  // Card form state
  const [cardData, setCardData] = useState({
    number: '',
    name: '',
    expiry: '',
    cvv: ''
  });
  const [cardError, setCardError] = useState<string | null>(null);

  const isIndianCurrency = currentCurrency.code === 'INR';

  const handleMethodSelect = (method: string) => {
    setSelectedMethod(method);
    if (method === 'card') {
      setIsUpiExpanded(false);
    }
  };

  const handleCardInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'number') {
      formattedValue = value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
      formattedValue = formattedValue.substring(0, 19);
    }

    if (name === 'expiry') {
      formattedValue = value
        .replace(/\D/g, '')
        .replace(/^(\d{2})/, '$1/')
        .substring(0, 5);
    }

    if (name === 'cvv') {
      formattedValue = value.replace(/\D/g, '').substring(0, 4);
    }

    setCardData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
    setCardError(null);
  };

  const handleCardSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCardError(null);

    if (!cardData.number || cardData.number.replace(/\s/g, '').length < 16) {
      setCardError('Please enter a valid card number');
      return;
    }

    if (!cardData.name) {
      setCardError('Please enter the cardholder name');
      return;
    }

    if (!cardData.expiry || !cardData.expiry.includes('/')) {
      setCardError('Please enter a valid expiry date');
      return;
    }

    if (!cardData.cvv || cardData.cvv.length < 3) {
      setCardError('Please enter a valid CVV');
      return;
    }

    onPaymentSelect('card');
  };

  const handleUpiSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpiError(null);

    if (!upiId) {
      setUpiError('Please enter a UPI ID');
      return;
    }

    const isValid = await validateUpiId(upiId);
    if (!isValid) {
      setUpiError('Invalid UPI ID format');
      return;
    }

    onPaymentSelect('upi');
  };

  const handlePhonePeError = (error: string) => {
    setCardError(error);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Select Payment Method</h3>

      {/* Credit/Debit Card Option */}
      <div
        onClick={() => handleMethodSelect('card')}
        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
          selectedMethod === 'card'
            ? 'border-yellow-500 bg-yellow-50'
            : 'border-gray-200 hover:border-yellow-500'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CreditCard className="h-6 w-6 text-gray-600" />
            <div>
              <p className="font-medium">Credit/Debit Card</p>
              <p className="text-sm text-gray-500">Visa, Mastercard, RuPay</p>
            </div>
          </div>
          {selectedMethod === 'card' && (
            <Check className="h-5 w-5 text-yellow-500" />
          )}
        </div>

        {selectedMethod === 'card' && (
          <div className="mt-4 pt-4 border-t">
            <form onSubmit={handleCardSubmit} className="space-y-4">
              <div>
                <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700">
                  Card Number
                </label>
                <div className="mt-1 relative">
                  <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    id="cardNumber"
                    name="number"
                    value={cardData.number}
                    onChange={handleCardInputChange}
                    placeholder="1234 5678 9012 3456"
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                    maxLength={19}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="cardName" className="block text-sm font-medium text-gray-700">
                  Cardholder Name
                </label>
                <div className="mt-1 relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    id="cardName"
                    name="name"
                    value={cardData.name}
                    onChange={handleCardInputChange}
                    placeholder="John Doe"
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="cardExpiry" className="block text-sm font-medium text-gray-700">
                    Expiry Date
                  </label>
                  <div className="mt-1 relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="cardExpiry"
                      name="expiry"
                      value={cardData.expiry}
                      onChange={handleCardInputChange}
                      placeholder="MM/YY"
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                      maxLength={5}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="cardCvv" className="block text-sm font-medium text-gray-700">
                    CVV
                  </label>
                  <div className="mt-1 relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="password"
                      id="cardCvv"
                      name="cvv"
                      value={cardData.cvv}
                      onChange={handleCardInputChange}
                      placeholder="123"
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                      maxLength={4}
                    />
                  </div>
                </div>
              </div>

              {cardError && (
                <div className="text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {cardError}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600 transition-colors"
              >
                Pay {currentCurrency.symbol}{amount} with Card
              </button>
            </form>
          </div>
        )}
      </div>

      {/* PhonePe Option - Show only for Indian currency */}
      {isIndianCurrency && (
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Smartphone className="h-6 w-6 text-purple-600" />
              <div>
                <p className="font-medium">PhonePe</p>
                <p className="text-sm text-gray-500">Pay using PhonePe UPI</p>
              </div>
            </div>
          </div>
          <PhonePeButton
            amount={amount}
            orderId={orderId}
            onSuccess={() => onPaymentSelect('phonepe')}
            onError={handlePhonePeError}
          />
        </div>
      )}

      {/* UPI Option - Show only for Indian currency */}
      {isIndianCurrency && (
        <div className="border rounded-lg">
          <div
            onClick={() => setIsUpiExpanded(!isUpiExpanded)}
            className={`p-4 cursor-pointer transition-colors ${
              selectedMethod.startsWith('upi')
                ? 'border-yellow-500 bg-yellow-50'
                : 'hover:border-yellow-500'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Smartphone className="h-6 w-6 text-gray-600" />
                <div>
                  <p className="font-medium">UPI</p>
                  <p className="text-sm text-gray-500">Google Pay, PhonePe, BHIM</p>
                </div>
              </div>
              <ChevronDown
                className={`h-5 w-5 text-gray-400 transform transition-transform ${
                  isUpiExpanded ? 'rotate-180' : ''
                }`}
              />
            </div>
          </div>

          {isUpiExpanded && (
            <div className="p-4 border-t">
              <form onSubmit={handleUpiSubmit} className="space-y-3">
                <div>
                  <label htmlFor="upiId" className="block text-sm font-medium text-gray-700">
                    Enter UPI ID
                  </label>
                  <div className="mt-1 relative">
                    <input
                      type="text"
                      id="upiId"
                      value={upiId}
                      onChange={(e) => {
                        setUpiId(e.target.value);
                        setUpiError(null);
                      }}
                      placeholder="username@upi"
                      className={`block w-full rounded-md shadow-sm ${
                        upiError
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-yellow-500 focus:ring-yellow-500'
                      }`}
                    />
                    {upiError && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <AlertCircle className="h-5 w-5 text-red-500" />
                      </div>
                    )}
                  </div>
                  {upiError && (
                    <p className="mt-1 text-sm text-red-600">{upiError}</p>
                  )}
                </div>
                <button
                  type="submit"
                  className="w-full bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600 transition-colors"
                >
                  Pay ₹{amount} with UPI
                </button>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Payment Amount Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="font-medium">Total Amount:</span>
          <span className="text-lg font-bold">
            {currentCurrency.symbol}{amount}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethods;